import { useMemo } from 'react';

import { isMac, isWindows } from 'utils/browser';

export type FontOS = 'macOS' | 'Windows';

export type FontOption = {
  label: string;
  fontFamily: string;
  os: FontOS[];
};

export type FontOptionWithId = FontOption & { id: string };

const fontOptions: Record<string, FontOption> = {
  default: {
    label: 'Default',
    fontFamily: 'default',
    os: ['macOS', 'Windows']
  },
  AvenirNext: {
    label: 'Avenir Next',
    fontFamily: '"Avenir Next", Avenir, "Helvetica Neue", Arial, sans-serif',
    os: ['macOS']
  },
  Courier: {
    label: 'Courier',
    fontFamily: '"Courier New", Courier, monospace',
    os: ['macOS', 'Windows']
  },
  Inter: {
    label: 'Inter',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    os: ['Windows']
  },
  Georgia: {
    label: 'Georgia',
    fontFamily: 'Georgia, "Times New Roman", Times, serif',
    os: ['macOS', 'Windows']
  },
  Lora: {
    label: 'Lora',
    fontFamily: 'Lora, Georgia, "Times New Roman", serif',
    os: ['Windows']
  },
  NewYork: {
    label: 'New York',
    fontFamily: '"New York", "Times New Roman", Times, serif',
    os: ['macOS']
  },
  OpenDyslexic: {
    label: 'OpenDyslexic',
    fontFamily: 'OpenDyslexic, Arial, sans-serif',
    os: ['macOS', 'Windows']
  },
  SourceCodePro: {
    label: 'Source Code Pro',
    fontFamily: '"Source Code Pro", "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
    os: ['Windows']
  },
  SourceSans: {
    label: 'Source Sans Pro',
    fontFamily: '"Source Sans 3", BlinkMacSystemFont, "Segoe UI", sans-serif',
    os: ['Windows']
  },
  SFMono: {
    label: 'SF Mono',
    fontFamily: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
    os: ['macOS']
  },
  SanFrancisco: {
    label: 'San Francisco',
    fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
    os: ['macOS']
  },
  TimesNewRoman: {
    label: 'Times New Roman',
    fontFamily: '"Times New Roman", Times, serif',
    os: ['macOS', 'Windows']
  }
};

export const DefaultClassicFontFamily = fontOptions.Georgia.fontFamily;

export const useFontOptions = (isEPUB: boolean = false) => {
  return useMemo(() => {
    const allFontEntries = Object.entries(fontOptions).map(([id, option]) => ({ id, ...option }));
    const filteredOptions = isEPUB ? allFontEntries : allFontEntries.filter(option => option.id !== 'default');

    const osFilteredOptions = (() => {
      if (isWindows()) {
        return filteredOptions.filter(option => option.os.includes('Windows'));
      }

      if (isMac()) {
        return filteredOptions.filter(option => option.os.includes('macOS'));
      }

      return filteredOptions;
    })();

    return osFilteredOptions;
  }, [isEPUB]);
};
